"use client";

import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('[ErrorBoundary] Caught error:', error);
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Error details:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    
    // Логируем дополнительную информацию
    if (error.message.includes('418')) {
      console.error('[ErrorBoundary] This is a hydration error (418)');
      console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
            <h1 className="text-xl font-semibold text-red-800 dark:text-red-400 mb-2">
              Произошла ошибка
            </h1>
            <p className="text-red-700 dark:text-red-300 mb-4">
              {this.state.error?.message || 'Неизвестная ошибка'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Перезагрузить страницу
            </button>
            {this.state.error && (
              <details className="mt-4 text-xs text-red-600 dark:text-red-400">
                <summary className="cursor-pointer">Подробности ошибки</summary>
                <pre className="mt-2 p-2 bg-red-100 dark:bg-red-900/40 rounded overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
